import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return undefined;
    const timer = window.setInterval(
      () => setIndex((i) => (i + 1) % items.length),
      6000,
    );
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;
  const active = items[index];

  const go = (next: number) =>
    setIndex((next + items.length) % items.length);

  return (
    <section
      className="relative h-[78vh] min-h-[560px] w-full overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {items.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={movie.heroImage}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} 百合影视`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/40" />

      <div className="relative mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-20 pt-28 md:px-6 md:pb-24">
        <div key={active.id} className="max-w-2xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-fire-fill" />
              本周焦点
            </span>
            <span className="rounded-full border border-white/30 px-3 py-1 text-xs text-white/85">
              {active.type}
            </span>
            <span className="rounded-full border border-white/30 px-3 py-1 text-xs text-white/85">
              {active.year} · {active.region}
            </span>
          </div>

          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-white md:text-5xl">
            {active.title}
          </h1>

          <div className="mt-3 flex items-center gap-2 text-sm text-white/90">
            <span className="flex h-4 w-4 items-center justify-center text-accent-300">
              <i className="ri-star-fill" />
            </span>
            <span className="font-semibold">{active.rating.toFixed(1)}</span>
            <span className="text-white/50">/ 10</span>
          </div>

          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/80 line-clamp-3 md:text-base">
            {active.synopsis}
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-play-circle-line" />
              </span>
              查看详情
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-white/40 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
            >
              浏览全部作品
            </Link>
          </div>
        </div>

        <div className="mt-10 flex items-center gap-4">
          <div className="flex items-center gap-2">
            {items.map((movie, i) => (
              <button
                key={movie.id}
                type="button"
                aria-label={`切换到 ${movie.title}`}
                onClick={() => go(i)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === index
                    ? "w-8 bg-accent-500"
                    : "w-4 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
          <div className="ml-auto hidden items-center gap-2 md:flex">
            <button
              type="button"
              aria-label="上一个"
              onClick={() => go(index - 1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/10"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <button
              type="button"
              aria-label="下一个"
              onClick={() => go(index + 1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/10"
            >
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}